import { useEffect, useRef, useState } from 'react';
import { FiCalendar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import styles from './YearPicker.module.css';
import { usePreferences } from '../../../contexts';


export interface YearPickerProps {
    years: number[];
    selectedYear: number;
    onChange: (year: number) => void;
    label?: string;
}

const YEARS_PER_PAGE = 12;

const computePageStart = (years: number[], selectedYear: number) => {
    const idx = years.indexOf(selectedYear);
    const base = idx === -1 ? years.length - YEARS_PER_PAGE : idx - (idx % YEARS_PER_PAGE);
    return Math.max(0, base);
};

export function YearPicker({ years, selectedYear, onChange, label = 'Ano' }: YearPickerProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [popupAlign, setPopupAlign] = useState<'left' | 'right'>('left');
    const [pageStart, setPageStart] = useState(() => computePageStart(years, selectedYear));
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const computeAlign = () => {
        if (!wrapperRef.current) return;
        const rect = wrapperRef.current.getBoundingClientRect();
        const spaceRight = window.innerWidth - rect.left;
        setPopupAlign(spaceRight >= 280 ? 'left' : 'right');
    };

    const toggleOpen = () => {
        if (isOpen) {
            setIsOpen(false);
            return;
        }
        computeAlign();
        // Recalcula a página exibida (sempre a que contém o ano selecionado) no momento da abertura
        setPageStart(computePageStart(years, selectedYear));
        setIsOpen(true);
    };

    const pick = (year: number) => {
        onChange(year);
        setIsOpen(false);
    };

    const currentYear = new Date().getFullYear();
    const visibleYears = years.slice(pageStart, pageStart + YEARS_PER_PAGE);
    const hasPrev = pageStart > 0;
    const hasNext = pageStart + YEARS_PER_PAGE < years.length;
    const { t } = usePreferences();

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <button
                type="button"
                className={`${styles.triggerField}${isOpen ? ` ${styles.active}` : ''}`}
                onClick={toggleOpen}
            >
                <FiCalendar size={13} className={styles.triggerIcon} />
                <span>
                    <span className={styles.triggerLabel}>{label}</span>
                    <span className={styles.triggerValue}>{selectedYear}</span>
                </span>
            </button>

            {isOpen && (
                <div
                    className={styles.popup}
                    style={popupAlign === 'right' ? { right: 0, left: 'auto' } : { left: 0, right: 'auto' }}
                >
                    <div className={styles.navRow}>
                        <button
                            type="button"
                            className={styles.navBtn}
                            onClick={() => setPageStart(p => Math.max(0, p - YEARS_PER_PAGE))}
                            disabled={!hasPrev}
                        >
                            <FiChevronLeft size={16} />
                        </button>
                        <span className={styles.navTitle}>
                            {visibleYears.length > 0 ? `${visibleYears[0]} - ${visibleYears[visibleYears.length - 1]}` : ''}
                        </span>
                        <button
                            type="button"
                            className={styles.navBtn}
                            onClick={() => setPageStart(p => p + YEARS_PER_PAGE)}
                            disabled={!hasNext}
                        >
                            <FiChevronRight size={16} />
                        </button>
                    </div>

                    <div className={styles.yearGrid}>
                        {visibleYears.map(year => (
                            <button
                                key={year}
                                type="button"
                                className={`${styles.yearCell}${year === selectedYear ? ` ${styles.isSelected}` : ''}`}
                                onClick={() => pick(year)}
                            >
                                {year}
                            </button>
                        ))}
                    </div>

                    {years.includes(currentYear) && currentYear !== selectedYear && (
                        <div className={styles.popupFooter}>
                            <button type="button" className={styles.footerBtn} onClick={() => pick(currentYear)}>
                                {t('common.anoAtual')}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}